import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
  BookmarkIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import {
  type SearchFilterFormData,
  type AdvancedSearchQuery,
  type FilterGroup,
  type FilterCondition,
  type SavedSearch,
  type SearchSuggestion,
  searchableFields,
  operatorsByType,
} from '../utils/schemas';
import type { SelectOption } from './ui/Select';

/** Form state that drives the UI only; never part of the search sent out. */
const FORM_INTERNAL_FIELDS = new Set([
  'advancedMode',
  'filterGroups',
  'savedSearchId',
]);

interface SearchAndFilterProps {
  onSearch: (filters: SearchFilterFormData | AdvancedSearchQuery) => void;
  onSuggestions?: (query: string) => Promise<SearchSuggestion[]>;
  statusOptions?: SelectOption[];
  typeOptions?: SelectOption[];
  showDateRange?: boolean;
  loading?: boolean;
  className?: string;
  enableAdvancedMode?: boolean;
  savedSearches?: SavedSearch[];
  onSaveSavedSearch?: (search: Omit<SavedSearch, 'id' | 'createdAt'>) => void;
  onDeleteSavedSearch?: (id: string) => void;
}

export const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  onSearch,
  onSuggestions,
  statusOptions = [],
  typeOptions = [],
  showDateRange = false,
  loading = false,
  className,
  enableAdvancedMode = true,
  savedSearches = [],
  onSaveSavedSearch,
  onDeleteSavedSearch,
}) => {
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<SearchFilterFormData>({
    // SPRITE-1468: no resolver. The old `resolver: searchFilterSchema` was a
    // plain object, not a resolver function, so every Search submit threw
    // "_options.resolver is not a function" and no list search ever ran.
    defaultValues: {
      search: '',
      status: '',
      type: '',
      startDate: '',
      endDate: '',
      advancedMode: false,
      filterGroups: [],
    },
  });

  const watchedValues = watch();
  // SPRITE-1468: `ref={searchInputRef}` after `{...register('search')}` used
  // to replace react-hook-form's ref, so the typed term never reached the
  // form and every Search submitted an empty query. Attach both refs.
  const { ref: registerSearchRef, ...searchFieldProps } = register('search');
  const attachSearchRef = useCallback(
    (element: HTMLInputElement | null) => {
      registerSearchRef(element);
      searchInputRef.current = element;
    },
    [registerSearchRef]
  );
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([
    {
      id: '1',
      logic: 'AND',
      conditions: [],
    },
  ]);

  const handleSearchInput = useCallback(
    async (value: string) => {
      if (value.length > 2 && onSuggestions) {
        try {
          const results = await onSuggestions(value);
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        }
      } else {
        setShowSuggestions(false);
      }
    },
    [onSuggestions]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (watchedValues.search) {
        handleSearchInput(watchedValues.search);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [watchedValues.search, handleSearchInput]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSubmit = (data: SearchFilterFormData) => {
    if (isAdvancedMode) {
      const advancedQuery: AdvancedSearchQuery = {
        globalSearch: data.search || undefined,
        filterGroups: filterGroups.filter(
          (group) => group.conditions.length > 0
        ),
        dateRange:
          data.startDate || data.endDate
            ? {
                field: 'createdAt',
                start: data.startDate,
                end: data.endDate,
              }
            : undefined,
      };
      onSearch(advancedQuery);
    } else {
      // SPRITE-1468: the form's own bookkeeping fields stay out of a simple
      // search. `filterGroups: []` used to leak through, and every consumer
      // took "has filterGroups" to mean "advanced query", so a plain search
      // term was dropped before it reached the server.
      const cleanedData = Object.fromEntries(
        Object.entries(data).filter(
          ([key, value]) =>
            !FORM_INTERNAL_FIELDS.has(key) &&
            value !== '' &&
            value !== undefined
        )
      );
      onSearch(cleanedData);
    }
    setShowSuggestions(false);
  };

  const handleReset = () => {
    reset();
    setFilterGroups([{ id: '1', logic: 'AND', conditions: [] }]);
    onSearch({});
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setValue('search', suggestion.value);
    setShowSuggestions(false);
    setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 100);
  };

  const addFilterGroup = () => {
    const newGroup: FilterGroup = {
      id: Date.now().toString(),
      logic: 'AND',
      conditions: [],
    };
    setFilterGroups([...filterGroups, newGroup]);
  };

  const removeFilterGroup = (groupId: string) => {
    if (filterGroups.length > 1) {
      setFilterGroups(filterGroups.filter((group) => group.id !== groupId));
    }
  };

  const updateFilterGroup = (
    groupId: string,
    updates: Partial<FilterGroup>
  ) => {
    setFilterGroups(
      filterGroups.map((group) =>
        group.id === groupId ? { ...group, ...updates } : group
      )
    );
  };

  const addCondition = (groupId: string) => {
    const newCondition: FilterCondition = {
      field: 'email',
      operator: 'contains',
      value: '',
      type: 'text',
    };

    setFilterGroups(
      filterGroups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: [...group.conditions, newCondition] }
          : group
      )
    );
  };

  const removeCondition = (groupId: string, conditionIndex: number) => {
    setFilterGroups(
      filterGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.filter(
                (_, index) => index !== conditionIndex
              ),
            }
          : group
      )
    );
  };

  const updateCondition = (
    groupId: string,
    conditionIndex: number,
    updates: Partial<FilterCondition>
  ) => {
    setFilterGroups(
      filterGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.map((condition, index) =>
                index === conditionIndex
                  ? { ...condition, ...updates }
                  : condition
              ),
            }
          : group
      )
    );
  };

  const handleSaveSearch = () => {
    if (!saveSearchName.trim() || !onSaveSavedSearch) return;

    const query: AdvancedSearchQuery = {
      globalSearch: watchedValues.search || undefined,
      filterGroups: filterGroups.filter((group) => group.conditions.length > 0),
      dateRange:
        watchedValues.startDate || watchedValues.endDate
          ? {
              field: 'createdAt',
              start: watchedValues.startDate,
              end: watchedValues.endDate,
            }
          : undefined,
    };

    onSaveSavedSearch({
      name: saveSearchName,
      query,
      lastUsed: new Date().toISOString(),
    });

    setSaveSearchName('');
    setShowSaveDialog(false);
  };

  const loadSavedSearch = (savedSearch: SavedSearch) => {
    const { query } = savedSearch;

    if (query.globalSearch) {
      setValue('search', query.globalSearch);
    }
    if (query.dateRange) {
      setValue('startDate', query.dateRange.start || '');
      setValue('endDate', query.dateRange.end || '');
    }

    setFilterGroups(
      query.filterGroups.length > 0
        ? query.filterGroups
        : [{ id: '1', logic: 'AND', conditions: [] }]
    );

    if (query.filterGroups.length > 0) {
      setIsAdvancedMode(true);
    }

    setShowSavedSearches(false);
  };

  const hasActiveFilters = isAdvancedMode
    ? filterGroups.some((group) => group.conditions.length > 0) ||
      watchedValues.search
    : Object.values(watchedValues).some(
        (value) => value !== '' && value !== undefined
      );

  const getFieldType = (fieldValue: string) => {
    const field = searchableFields.find((f) => f.value === fieldValue);
    return field?.type || 'text';
  };

  return (
    <div
      className={`bg-slate-900/40 border border-white/5 backdrop-blur-xl shadow-2xl rounded-2xl p-6 space-y-4 ${className}`}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-bold text-white tracking-wide">
              Filters & Search
            </h3>
            {enableAdvancedMode && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedMode(!isAdvancedMode)}
                className="text-blue-400 hover:text-blue-300"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                {isAdvancedMode ? 'Simple Mode' : 'Advanced Mode'}
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {savedSearches.length > 0 && (
              <div className="relative">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSavedSearches(!showSavedSearches)}
                  className="border-white/10 hover:bg-white/5"
                >
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Saved Searches
                  <ChevronDownIcon className="h-4 w-4 ml-2" />
                </Button>
                {showSavedSearches && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 space-y-1">
                      {savedSearches.map((savedSearch) => (
                        <div
                          key={savedSearch.id}
                          className="flex items-center justify-between p-2 hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          <button
                            type="button"
                            onClick={() => loadSavedSearch(savedSearch)}
                            className="flex-1 text-left text-sm text-slate-200 hover:text-white"
                          >
                            {savedSearch.name}
                          </button>
                          {onDeleteSavedSearch && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                onDeleteSavedSearch(savedSearch.id)
                              }
                              className="text-rose-400 hover:text-rose-300 transition-colors"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {onSaveSavedSearch && hasActiveFilters && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowSaveDialog(true)}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              >
                <BookmarkIcon className="h-4 w-4 mr-2" />
                Save Search
              </Button>
            )}
          </div>
        </div>

        <div className="relative">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Input
                {...searchFieldProps}
                ref={attachSearchRef}
                placeholder="Search..."
                error={errors.search?.message}
                className="pl-11 bg-slate-900/50 border-white/10"
                onFocus={() => {
                  if (suggestions.length > 0) setShowSuggestions(true);
                }}
              />
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />

              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto"
                >
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-700 flex items-center justify-between transition-colors border-b border-white/5 last:border-0"
                    >
                      <div>
                        <div className="text-sm font-medium text-white">
                          {suggestion.label}
                        </div>
                        {suggestion.context && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {suggestion.context}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-blue-400 font-semibold uppercase">
                        {suggestion.type}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20 px-6"
            >
              Search
            </Button>
          </div>
        </div>

        {!isAdvancedMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {statusOptions.length > 0 && (
              <Select
                label="Status"
                options={[
                  { label: 'All Statuses', value: '' },
                  ...statusOptions,
                ]}
                value={watchedValues.status || ''}
                onChange={(value) => setValue('status', value)}
                error={errors.status?.message}
              />
            )}
            {typeOptions.length > 0 && (
              <Select
                label="Type"
                options={[{ label: 'All Types', value: '' }, ...typeOptions]}
                value={watchedValues.type || ''}
                onChange={(value) => setValue('type', value)}
                error={errors.type?.message}
              />
            )}
            {showDateRange && (
              <>
                <Input
                  {...register('startDate')}
                  type="date"
                  label="Start Date"
                  error={errors.startDate?.message}
                />
                <Input
                  {...register('endDate')}
                  type="date"
                  label="End Date"
                  error={errors.endDate?.message}
                />
              </>
            )}
          </div>
        )}

        {isAdvancedMode && (
          <div className="space-y-4">
            {filterGroups.map((group, groupIndex) => (
              <div
                key={group.id}
                className="border border-white/10 bg-slate-800/30 rounded-xl p-5 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {groupIndex > 0 && (
                      <Select
                        options={[
                          { label: 'AND', value: 'AND' },
                          { label: 'OR', value: 'OR' },
                        ]}
                        value={group.logic}
                        onChange={(value) =>
                          updateFilterGroup(group.id, {
                            logic: value as 'AND' | 'OR',
                          })
                        }
                      />
                    )}
                    <span className="text-sm font-medium text-slate-400 uppercase tracking-widest">
                      Group {groupIndex + 1}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addCondition(group.id)}
                      className="border-white/10 hover:bg-white/5"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" /> Add Condition
                    </Button>
                    {filterGroups.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeFilterGroup(group.id)}
                        className="border-white/10 text-rose-400 hover:bg-rose-500/10"
                      >
                        {' '}
                        <XMarkIcon className="h-4 w-4" />{' '}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {group.conditions.map((condition, conditionIndex) => (
                    <div
                      key={conditionIndex}
                      className="grid grid-cols-12 gap-3 items-end"
                    >
                      {conditionIndex > 0 && (
                        <div className="col-span-1 text-center text-sm font-bold text-slate-500">
                          {group.logic}
                        </div>
                      )}
                      <div
                        className={
                          conditionIndex === 0 ? 'col-span-3' : 'col-span-2'
                        }
                      >
                        <Select
                          label={conditionIndex === 0 ? 'Field' : undefined}
                          options={searchableFields.map((field) => ({
                            label: field.label,
                            value: field.value,
                          }))}
                          value={condition.field}
                          onChange={(value) => {
                            const fieldType = getFieldType(value);
                            const defaultOperator =
                              operatorsByType[fieldType]?.[0]?.value ||
                              'contains';
                            updateCondition(group.id, conditionIndex, {
                              field: value,
                              type: fieldType,
                              operator: defaultOperator as any,
                              value: '',
                            });
                          }}
                        />
                      </div>
                      <div className="col-span-2">
                        <Select
                          label={conditionIndex === 0 ? 'Operator' : undefined}
                          options={
                            operatorsByType[condition.type] ||
                            operatorsByType.text
                          }
                          value={condition.operator}
                          onChange={(value) =>
                            updateCondition(group.id, conditionIndex, {
                              operator: value as any,
                            })
                          }
                        />
                      </div>
                      <div className="col-span-3">
                        {condition.type === 'date' ? (
                          <Input
                            type="date"
                            label={conditionIndex === 0 ? 'Value' : undefined}
                            value={condition.value as string}
                            onChange={(e) =>
                              updateCondition(group.id, conditionIndex, {
                                value: e.target.value,
                              })
                            }
                          />
                        ) : condition.type === 'number' ? (
                          <Input
                            type="number"
                            label={conditionIndex === 0 ? 'Value' : undefined}
                            value={condition.value as string}
                            onChange={(e) =>
                              updateCondition(group.id, conditionIndex, {
                                value: e.target.value,
                              })
                            }
                          />
                        ) : condition.type === 'select' &&
                          condition.field === 'status' ? (
                          <Select
                            label={conditionIndex === 0 ? 'Value' : undefined}
                            options={statusOptions}
                            value={condition.value as string}
                            onChange={(value) =>
                              updateCondition(group.id, conditionIndex, {
                                value,
                              })
                            }
                          />
                        ) : (
                          <Input
                            label={conditionIndex === 0 ? 'Value' : undefined}
                            value={condition.value as string}
                            onChange={(e) =>
                              updateCondition(group.id, conditionIndex, {
                                value: e.target.value,
                              })
                            }
                            placeholder="Enter value..."
                          />
                        )}
                      </div>
                      <div className="col-span-1 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            removeCondition(group.id, conditionIndex)
                          }
                          className="border-white/10 text-rose-400 hover:bg-rose-500/10"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {group.conditions.length === 0 && (
                    <div className="text-center py-6 text-slate-500 italic">
                      No conditions added.
                    </div>
                  )}
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addFilterGroup}
              className="border-dashed border-white/20 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/50 w-full p-4 mt-2"
            >
              <PlusIcon className="h-5 w-5 mr-2" /> Add Filter Group
            </Button>
          </div>
        )}

        {isAdvancedMode && showDateRange && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Input
              {...register('startDate')}
              type="date"
              label="Start Date"
              error={errors.startDate?.message}
            />
            <Input
              {...register('endDate')}
              type="date"
              label="End Date"
              error={errors.endDate?.message}
            />
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-white/5 mt-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon
              className={
                hasActiveFilters
                  ? 'h-5 w-5 text-blue-400'
                  : 'h-5 w-5 text-slate-600'
              }
            />
            <span
              className={
                hasActiveFilters
                  ? 'text-sm font-semibold text-blue-400'
                  : 'text-sm text-slate-500'
              }
            >
              {hasActiveFilters ? 'Filters applied' : 'No filters applied'}
            </span>
          </div>
          <div className="flex space-x-3">
            {hasActiveFilters && (
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={loading}
                className="border-white/10 hover:bg-white/5"
              >
                Clear All
              </Button>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20 px-6"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </form>

      {showSaveDialog && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-96 shadow-2xl animate-fade-in">
            <h3 className="text-xl font-bold text-white mb-6">Save Search</h3>
            <Input
              label="Search Name"
              value={saveSearchName}
              onChange={(e) => setSaveSearchName(e.target.value)}
              placeholder="Enter a name..."
              className="mb-6"
            />
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowSaveDialog(false);
                  setSaveSearchName('');
                }}
                className="border-white/10"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveSearch}
                disabled={!saveSearchName.trim()}
                className="bg-blue-600 hover:bg-blue-500"
              >
                Save Search
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
